import { useEffect, useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { ErrorOwner } from '../../api/api';
import {
    CustomerNotice,
    DailyReportGroup,
    DailyReportObject,
    STATUS_LABEL,
} from '../../api/report';
import { RequeueItem } from '../../api/requeue';
import { DailyReportMonitor } from '../../hooks/useDailyReport';
import { CollapsibleSection } from '../Section/CollapsibleSection';
import { TraceCallout } from '../Trace/TraceCallout';
import {
    InterpretationNote,
    OwnerBadge,
} from '../Interpretation/InterpretationNote';
import './DailyReport.scss';

/** How many objects of a group are listed before "toon alle". */
const SHOWN = 12;

const isoDay = (date: Date): string =>
    `${date.getFullYear()}-${`${date.getMonth() + 1}`.padStart(
        2,
        '0'
    )}-${`${date.getDate()}`.padStart(2, '0')}`;

const shift = (date: Date, days: number): Date => {
    const next = new Date(date);
    next.setDate(next.getDate() + days);

    return next;
};

const time = (iso?: string | null): string =>
    iso
        ? new Date(iso).toLocaleTimeString('nl-NL', {
              hour: '2-digit',
              minute: '2-digit',
          })
        : '';

/** The objects of a group that offering again can get through. */
const requeueable = (group: DailyReportGroup): DailyReportObject[] =>
    group.requeueHelps
        ? group.objects.filter(
              (o) =>
                  o.status !== 'Resolved' &&
                  o.status !== 'WaitingOnCustomer' &&
                  !!o.objectGuid
          )
        : [];

export interface IDailyReportPanelProps {
    monitor: DailyReportMonitor;
    open: boolean;
    onToggle: () => void;
    /** Offers objects again through the error log's requeue, guard and all. */
    onRequeue: (items: RequeueItem[]) => void;
    requeueBusy: boolean;
    /** Opens the dossier of one object. */
    onDossier: (id: string) => void;
}

/**
 * What went wrong on one day, why, what has become of it since, and whether the customer has
 * to hear about it - with the message ready to copy.
 *
 * Read live: "opgelost" means solved by now. Solving is one click per kind of problem, not
 * something this does by itself: offering messages again on a production queue is a decision
 * somebody takes, with the guard in between that refuses what cannot work.
 */
export const DailyReportPanel = ({
    monitor,
    open,
    onToggle,
    onRequeue,
    requeueBusy,
    onDossier,
}: IDailyReportPanelProps) => {
    const { report, date, loading, error, load } = monitor;

    // Opening the section is what asks for the first report; nothing is read before that.
    useEffect(() => {
        if (open && !report && !loading && !error) {
            load(date);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open]);

    const today = new Date();
    today.setHours(0, 0, 0, 0);

    return (
        <CollapsibleSection
            id="daily-report"
            title="Dagoverzicht"
            hint="Wat er op één dag misging, wat daar inmiddels mee is gebeurd, en wat de klant moet weten"
            open={open}
            onToggle={onToggle}
            busy={loading}
            status={
                report
                    ? report.totals.objects === 0
                        ? 'niets misgegaan'
                        : `${report.totals.resolved} opgelost, ${
                              report.totals.objects - report.totals.resolved
                          } open`
                    : undefined
            }
            tools={
                <div className="daily-report__date">
                    <button
                        type="button"
                        className="btn btn-sm btn-outline-secondary"
                        onClick={() => load(shift(date, -1))}
                        aria-label="Vorige dag"
                        disabled={loading}
                    >
                        <FontAwesomeIcon icon="arrow-left" />
                    </button>
                    <input
                        type="date"
                        className="form-control daily-report__date-input"
                        value={isoDay(date)}
                        max={isoDay(today)}
                        onChange={(e) => {
                            if (e.target.value) {
                                load(new Date(`${e.target.value}T00:00:00`));
                            }
                        }}
                        aria-label="Dag"
                    />
                    <button
                        type="button"
                        className="btn btn-sm btn-outline-secondary"
                        onClick={() => load(shift(date, 1))}
                        aria-label="Volgende dag"
                        disabled={loading || shift(date, 1) > today}
                    >
                        <FontAwesomeIcon icon="arrow-right" />
                    </button>
                    <button
                        type="button"
                        className="btn btn-sm btn-outline-secondary"
                        onClick={() => load(today)}
                        disabled={loading}
                    >
                        Vandaag
                    </button>
                </div>
            }
        >
            {error && (
                <TraceCallout tone="danger" title="Ophalen mislukt">
                    {error}
                </TraceCallout>
            )}

            {loading && (
                <div className="daily-report" role="status">
                    <p className="daily-report__muted">
                        <FontAwesomeIcon icon="spinner" spin /> Dagoverzicht van{' '}
                        {date.toLocaleDateString('nl-NL')} ophalen.
                    </p>
                    {[0, 1, 2].map((i) => (
                        <div key={i} className="daily-report__skeleton" />
                    ))}
                </div>
            )}

            {report && !loading && (
                <div className="daily-report">
                    <p className="daily-report__summary">{report.summary}</p>

                    {report.totals.objects > 0 && (
                        <ul className="daily-report__totals">
                            <Total
                                label="Opgelost"
                                value={report.totals.resolved}
                                tone="ok"
                            />
                            <Total
                                label="Wacht op klant"
                                value={report.totals.waitingOnCustomer}
                                tone="warn"
                            />
                            <Total
                                label="Wacht op ons"
                                value={report.totals.waitingOnUs}
                                tone="error"
                            />
                            <Total
                                label="Opnieuw aanbieden"
                                value={report.totals.needsRequeue}
                                tone="info"
                            />
                            <Total
                                label="Wordt nog geprobeerd"
                                value={report.totals.resolvesItself}
                                tone="muted"
                            />
                        </ul>
                    )}

                    {report.groups.length > 0 && (
                        <ul className="daily-report__groups">
                            {report.groups.map((group) => (
                                <GroupRow
                                    key={group.code}
                                    group={group}
                                    onRequeue={onRequeue}
                                    requeueBusy={requeueBusy}
                                    onDossier={onDossier}
                                />
                            ))}
                        </ul>
                    )}

                    <CustomerNoticeCard notice={report.customerNotice} />
                </div>
            )}
        </CollapsibleSection>
    );
};

const Total = ({
    label,
    value,
    tone,
}: {
    label: string;
    value: number;
    tone: string;
}) =>
    value > 0 ? (
        <li className={`daily-report__total daily-report__total--${tone}`}>
            <strong>{value}</strong> {label.toLowerCase()}
        </li>
    ) : null;

interface IGroupRowProps {
    group: DailyReportGroup;
    onRequeue: (items: RequeueItem[]) => void;
    requeueBusy: boolean;
    onDossier: (id: string) => void;
}

const GroupRow = ({
    group,
    onRequeue,
    requeueBusy,
    onDossier,
}: IGroupRowProps) => {
    const [open, setOpen] = useState<boolean>(false);
    const [all, setAll] = useState<boolean>(false);
    const offer = requeueable(group);
    const objects = all ? group.objects : group.objects.slice(0, SHOWN);
    const owner =
        group.open === 0 ? ErrorOwner.None : group.interpretation?.owner;

    return (
        <li className="daily-report__group">
            <div className="daily-report__group-head">
                <button
                    type="button"
                    className="daily-report__expand"
                    onClick={() => setOpen(!open)}
                    aria-expanded={open}
                    aria-label={open ? 'Inklappen' : 'Objecten tonen'}
                >
                    <FontAwesomeIcon
                        icon={open ? 'angle-down' : 'angle-right'}
                    />
                </button>

                <OwnerBadge owner={owner} />

                <div className="daily-report__group-text">
                    <span className="daily-report__group-title">
                        {group.title}
                    </span>
                    {group.open > 0 && group.interpretation?.action && (
                        <span className="daily-report__muted">
                            {group.interpretation.action}
                        </span>
                    )}
                </div>

                <span className="daily-report__counts">
                    {group.open > 0 && (
                        <span className="daily-report__open">
                            {group.open} open
                        </span>
                    )}
                    {group.resolved > 0 && (
                        <span>{group.resolved} opgelost</span>
                    )}
                </span>

                {offer.length > 0 && (
                    <button
                        type="button"
                        className="btn btn-sm btn-outline-primary"
                        disabled={requeueBusy}
                        onClick={() =>
                            onRequeue(
                                offer.map((o) => ({
                                    objectType: o.objectType,
                                    eventType: o.eventType,
                                    objectGuid: o.objectGuid as string,
                                    relationId: o.relationId,
                                }))
                            )
                        }
                        title="Zet deze objecten opnieuw op de queue. Wat niet kan slagen, houdt de controle tegen."
                    >
                        Opnieuw aanbieden ({offer.length})
                    </button>
                )}
            </div>

            {open && (
                <div className="daily-report__group-body">
                    {group.interpretation && (
                        <InterpretationNote
                            interpretation={group.interpretation}
                            bodyOnly
                        />
                    )}

                    <table className="daily-report__table">
                        <thead>
                            <tr>
                                <th>Object</th>
                                <th>Stand</th>
                                <th>Pogingen</th>
                                <th>Laatst mislukt</th>
                                <th>Toelichting</th>
                                <th aria-label="Acties" />
                            </tr>
                        </thead>
                        <tbody>
                            {objects.map((o) => (
                                <tr key={`${o.objectType}|${o.relationId}`}>
                                    <td>
                                        <span className="daily-report__type">
                                            {o.objectType}
                                        </span>
                                        <span className="daily-report__id">
                                            {o.relationId}
                                        </span>
                                    </td>
                                    <td>
                                        <span
                                            className={`daily-report__status daily-report__status--${o.status}`}
                                        >
                                            {STATUS_LABEL[o.status]}
                                        </span>
                                    </td>
                                    <td>{o.attempts}</td>
                                    <td>{time(o.lastFailedAt)}</td>
                                    <td className="daily-report__note">
                                        {o.resolution ??
                                            (o.status !== 'Resolved'
                                                ? o.interpretation?.action
                                                : null)}
                                    </td>
                                    <td>
                                        <button
                                            type="button"
                                            className="daily-report__link"
                                            onClick={() =>
                                                onDossier(o.relationId)
                                            }
                                        >
                                            Dossier
                                        </button>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>

                    {group.objects.length > SHOWN && (
                        <button
                            type="button"
                            className="daily-report__link"
                            onClick={() => setAll(!all)}
                        >
                            {all
                                ? 'Minder tonen'
                                : `Alle ${group.objects.length} tonen`}
                        </button>
                    )}
                </div>
            )}
        </li>
    );
};

const CustomerNoticeCard = ({ notice }: { notice: CustomerNotice }) => {
    const [language, setLanguage] = useState<'nl' | 'en'>('nl');
    const [copied, setCopied] = useState<string | null>(null);

    if (!notice.needed) {
        return (
            <p className="daily-report__muted">
                <FontAwesomeIcon icon="circle-check" /> Klant hoeft niets te
                weten. {notice.reason}
            </p>
        );
    }

    const subject =
        (language === 'nl' ? notice.subjectNl : notice.subjectEn) ?? '';
    const body = (language === 'nl' ? notice.bodyNl : notice.bodyEn) ?? '';

    const copy = (what: string, text: string) => {
        navigator.clipboard
            ?.writeText(text)
            .then(() => {
                setCopied(what);
                window.setTimeout(() => setCopied(null), 2000);
            })
            .catch(() => setCopied(null));
    };

    return (
        <section className="daily-report__notice" aria-label="Klant informeren">
            <div className="daily-report__notice-head">
                <h4 className="daily-report__heading">
                    <FontAwesomeIcon icon="envelope" /> Klant informeren
                </h4>
                <div
                    className="daily-report__languages"
                    role="group"
                    aria-label="Taal"
                >
                    <button
                        type="button"
                        className={`btn btn-sm ${
                            language === 'nl'
                                ? 'btn-primary'
                                : 'btn-outline-secondary'
                        }`}
                        onClick={() => setLanguage('nl')}
                    >
                        Nederlands
                    </button>
                    <button
                        type="button"
                        className={`btn btn-sm ${
                            language === 'en'
                                ? 'btn-primary'
                                : 'btn-outline-secondary'
                        }`}
                        onClick={() => setLanguage('en')}
                    >
                        English
                    </button>
                </div>
            </div>

            <p className="daily-report__muted">
                {notice.reason} Lees de tekst na en vul je naam in voordat je
                hem verstuurt.
            </p>

            <label className="daily-report__label" htmlFor="notice-subject">
                Onderwerp
            </label>
            <div className="daily-report__copy-row">
                <input
                    id="notice-subject"
                    className="form-control"
                    readOnly
                    value={subject}
                />
                <button
                    type="button"
                    className="btn btn-sm btn-outline-secondary"
                    onClick={() => copy('subject', subject)}
                >
                    <FontAwesomeIcon icon="copy" />{' '}
                    {copied === 'subject' ? 'Gekopieerd' : 'Kopiëren'}
                </button>
            </div>

            <label className="daily-report__label" htmlFor="notice-body">
                Tekst
            </label>
            <textarea
                id="notice-body"
                className="form-control daily-report__body"
                readOnly
                value={body}
                rows={Math.min(18, body.split('\n').length + 1)}
            />
            <div>
                <button
                    type="button"
                    className="btn btn-sm btn-outline-secondary"
                    onClick={() => copy('body', body)}
                >
                    <FontAwesomeIcon icon="copy" />{' '}
                    {copied === 'body' ? 'Gekopieerd' : 'Tekst kopiëren'}
                </button>
            </div>
        </section>
    );
};
